import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';
import '../messages/ThreadQuill.css';
import url from '../constants/config';
import routes from '../routesDefinitions/routes';

/* ─── Custom image blot — preserves style attribute ─────────────────────── */

const BaseImage = Quill.import('formats/image');

class StyledImage extends BaseImage {
  static formats(domNode) {
    const base = super.formats(domNode);
    const style = domNode.getAttribute('style');
    if (style) base.style = style;
    return base;
  }

  format(name, value) {
    if (name === 'style') {
      if (value) this.domNode.setAttribute('style', value);
      else this.domNode.removeAttribute('style');
    } else {
      super.format(name, value);
    }
  }
}

StyledImage.blotName = 'image';
StyledImage.tagName = 'IMG';
Quill.register(StyledImage, true);

/* ─── Auth ───────────────────────────────────────────────────────────────── */

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

/* ─── Toolbar config ─────────────────────────────────────────────────────── */

const QUILL_TOOLBAR_CONTAINER = [
  [{ header: [1, 2, 3, false] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean'],
];

const quillFormats = [
  'header', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'indent',
  'blockquote', 'code-block',
  'link', 'image', 'style',
];

/* ─── Image alignment helpers ────────────────────────────────────────────── */

function getStyleProp(styleStr, prop) {
  const m = styleStr.match(new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'i'));
  return m ? m[1].trim() : '';
}

function removeStyleProps(styleStr, props) {
  let s = styleStr;
  props.forEach(p => { s = s.replace(new RegExp(`${p}\\s*:[^;]+;?\\s*`, 'gi'), ''); });
  return s.trim();
}

function getImgAlignment(imgEl) {
  const s = imgEl.getAttribute('style') || '';
  if (/float\s*:\s*left/i.test(s)) return 'left';
  if (/float\s*:\s*right/i.test(s)) return 'right';
  if (/margin\s*:\s*\S*\s*auto/i.test(s) || /margin-left\s*:\s*auto/i.test(s)) return 'center';
  return 'none';
}

function buildAlignStyle(imgEl, aln) {
  const current = imgEl.getAttribute('style') || '';
  // Preserve width/height, strip old alignment props
  const w = getStyleProp(current, 'width');
  const h = getStyleProp(current, 'height');
  let base = '';
  if (aln === 'left')   base = 'float: left; margin: 0 16px 8px 0;';
  if (aln === 'right')  base = 'float: right; margin: 0 0 8px 16px;';
  if (aln === 'center') base = 'display: block; margin: 8px auto;';
  if (w) base += ` width: ${w};`;
  if (h && h !== 'auto') base += ` height: ${h};`;
  return base.trim();
}

function buildResizeStyle(imgEl, newWidth) {
  const current = imgEl.getAttribute('style') || '';
  const stripped = removeStyleProps(current, ['width', 'height']);
  return `${stripped}${stripped ? ' ' : ''}width: ${newWidth}px; height: auto;`.trim();
}

/* ─── ImageResizer overlay ───────────────────────────────────────────────── */

const ImageResizer = ({ imgEl, quillEditor, onClose, onDelete }) => {
  const [rect, setRect] = useState(() => imgEl.getBoundingClientRect());
  const [aln, setAln] = useState(() => getImgAlignment(imgEl));
  const [widthLabel, setWidthLabel] = useState(() => Math.round(imgEl.offsetWidth));

  // Keep position in sync with scroll / resize
  useEffect(() => {
    const update = () => setRect(imgEl.getBoundingClientRect());
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [imgEl]);

  // --- Alignment ---
  const handleAlign = useCallback((newAln) => {
    const newStyle = buildAlignStyle(imgEl, newAln);
    const blot = Quill.find(imgEl);
    if (blot) blot.format('style', newStyle || null);
    setAln(newAln);
    // Rect updates after Quill re-renders
    requestAnimationFrame(() => setRect(imgEl.getBoundingClientRect()));
  }, [imgEl]);

  // --- Resize by drag ---
  const startResize = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!quillEditor) return;

    quillEditor.enable(false); // disconnect MutationObserver during drag

    const startX = e.clientX;
    const startWidth = imgEl.offsetWidth;

    const onMove = (ev) => {
      const w = Math.max(40, startWidth + (ev.clientX - startX));
      imgEl.style.width = w + 'px';
      imgEl.style.height = 'auto';
      setWidthLabel(Math.round(w));
      setRect(imgEl.getBoundingClientRect());
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);

      quillEditor.enable(true); // reconnect observer

      // Commit via blot so Quill's Delta + react-quill state update properly
      const finalStyle = buildResizeStyle(imgEl, Math.round(imgEl.offsetWidth));
      const blot = Quill.find(imgEl);
      if (blot) blot.format('style', finalStyle);

      requestAnimationFrame(() => setRect(imgEl.getBoundingClientRect()));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [imgEl, quillEditor]);

  const toolbarTop = rect.top >= 46 ? rect.top - 42 : rect.bottom + 6;

  return ReactDOM.createPortal(
    <>
      {/* Selection border */}
      <div
        style={{
          position: 'fixed',
          top: rect.top - 2,
          left: rect.left - 2,
          width: rect.width + 4,
          height: rect.height + 4,
          border: '2px solid #2e496b',
          borderRadius: '2px',
          pointerEvents: 'none',
          zIndex: 9998,
        }}
      />

      {/* Floating toolbar */}
      <div
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: toolbarTop,
          left: rect.left,
          zIndex: 9999,
          background: '#1e293b',
          borderRadius: '6px',
          padding: '3px 5px',
          display: 'flex',
          alignItems: 'center',
          gap: '1px',
          boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
          userSelect: 'none',
        }}
      >
        {[
          { id: 'left',   icon: 'fa-align-left',    title: 'Lewo' },
          { id: 'center', icon: 'fa-align-center',  title: 'Środek' },
          { id: 'right',  icon: 'fa-align-right',   title: 'Prawo' },
          { id: 'none',   icon: 'fa-align-justify', title: 'Brak' },
        ].map(({ id, icon, title }) => (
          <button
            key={id}
            type="button"
            title={title}
            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); handleAlign(id); }}
            style={{
              padding: '5px 9px',
              background: aln === id ? '#334155' : 'transparent',
              color: aln === id ? '#60a5fa' : '#cbd5e1',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              lineHeight: 1,
            }}
          >
            <i className={`fas ${icon}`} />
          </button>
        ))}

        <div style={{ width: 1, height: 18, background: '#334155', margin: '0 4px' }} />

        <span style={{ color: '#94a3b8', fontSize: '11px', padding: '0 4px', minWidth: 36, textAlign: 'center' }}>
          {widthLabel}px
        </span>

        <div style={{ width: 1, height: 18, background: '#334155', margin: '0 4px' }} />

        <button
          type="button"
          title="Usuń obraz"
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
          style={{
            padding: '5px 7px',
            background: 'transparent',
            color: '#fca5a5',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            lineHeight: 1,
          }}
        >
          <i className="fas fa-trash-alt" />
        </button>

        <button
          type="button"
          title="Zamknij"
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          style={{
            padding: '5px 7px',
            background: 'transparent',
            color: '#64748b',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            lineHeight: 1,
          }}
        >
          <i className="fas fa-times" />
        </button>
      </div>

      {/* Resize handle — bottom-right corner */}
      <div
        onMouseDown={startResize}
        style={{
          position: 'fixed',
          top: rect.bottom - 8,
          left: rect.right - 8,
          width: 16,
          height: 16,
          background: '#2e496b',
          border: '2.5px solid #ffffff',
          borderRadius: '3px',
          cursor: 'nwse-resize',
          zIndex: 9999,
          boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        }}
      />
    </>,
    document.body
  );
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const countWords = html => {
  const text = (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.split(' ').length : 0;
};

const formatDate = iso => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const VIEWS = { EDIT: 'edit', SPLIT: 'split', PREVIEW: 'preview' };

/* ─── AdminPageContent ───────────────────────────────────────────────────── */

const AdminPageContent = () => {
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [isVisibleToPlayers, setIsVisibleToPlayers] = useState(false);
  const [savedIsVisibleToPlayers, setSavedIsVisibleToPlayers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(VIEWS.SPLIT);
  const [selectedImg, setSelectedImg] = useState(null);
  const quillRef = useRef(null);

  const isDirty = content !== savedContent || isVisibleToPlayers !== savedIsVisibleToPlayers;

  /* ─── Image upload handler ─── */
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const editor = quillRef.current && quillRef.current.getEditor();
      const range = editor ? editor.getSelection(true) : null;
      const formData = new FormData();
      formData.append('image', file);
      formData.append('pageKey', 'player_info');
      try {
        const res = await axios.post(
          `${url}/admin/page-content-images`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );
        if (editor && range) {
          editor.insertEmbed(range.index, 'image', res.data.url);
          editor.setSelection(range.index + 1, 0);
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Nie udało się wgrać obrazu.';
        setError(msg);
      }
    };
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: QUILL_TOOLBAR_CONTAINER,
      handlers: { image: imageHandler },
    },
  }), [imageHandler]);

  /* ─── Load content ─── */
  useEffect(() => {
    axios
      .get(`${url}/player-portal/info`, { headers: getAuthHeader() })
      .then(res => {
        const c = res.data.content || '';
        const visible = Boolean(res.data.isVisibleToPlayers);
        setContent(c);
        setSavedContent(c);
        setIsVisibleToPlayers(visible);
        setSavedIsVisibleToPlayers(visible);
        setSavedAt(res.data.updatedAt || null);
      })
      .catch(() => setError('Nie udało się załadować treści.'))
      .finally(() => setLoading(false));
  }, []);

  /* ─── Save ─── */
  const handleSave = useCallback(() => {
    if (saving || loading) return;
    setSaving(true);
    setError(null);
    axios
      .put(
        `${url}/admin/page-content/player_info`,
        { content, isVisibleToPlayers },
        { headers: getAuthHeader() }
      )
      .then(res => {
        setSavedAt(res.data.updatedAt);
        setSavedContent(content);
        setSavedIsVisibleToPlayers(Boolean(res.data.isVisibleToPlayers));
      })
      .catch(() => setError('Nie udało się zapisać. Spróbuj ponownie.'))
      .finally(() => setSaving(false));
  }, [content, isVisibleToPlayers, saving, loading]);

  const handleDeleteSelectedImage = useCallback(() => {
    if (!selectedImg || !quillRef.current) return;
    const editor = quillRef.current.getEditor();
    const blot = Quill.find(selectedImg);
    if (!editor || !blot) return;
    const index = editor.getIndex(blot);
    blot.remove();
    editor.update('user');
    editor.setSelection(Math.max(index, 0), 0, 'silent');
    setSelectedImg(null);
  }, [selectedImg]);

  // Ctrl+S / Cmd+S
  useEffect(() => {
    const handleKeyDown = e => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  /* ─── Image click — select for resize/align ─── */
  useEffect(() => {
    if (loading) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const handleClick = e => {
      if (e.target.tagName === 'IMG') {
        e.stopPropagation();
        setSelectedImg(e.target);
      }
    };
    editor.root.addEventListener('click', handleClick);
    return () => editor.root.removeEventListener('click', handleClick);
  }, [loading]);

  // Deselect when clicking outside the image
  useEffect(() => {
    if (!selectedImg) return;
    const handleDocClick = () => setSelectedImg(null);
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [selectedImg]);

  // Deselect when switching to preview-only mode
  useEffect(() => {
    if (view === VIEWS.PREVIEW) setSelectedImg(null);
  }, [view]);

  const wordCount = countWords(content);
  const showEditor = view === VIEWS.EDIT || view === VIEWS.SPLIT;
  const showPreview = view === VIEWS.PREVIEW || view === VIEWS.SPLIT;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 80px)' }}>

      {/* ── Top bar ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 24px',
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: '10px',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        {/* Left: title + meta */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '17px', fontWeight: 700, whiteSpace: 'nowrap' }}>
            <i className="fas fa-book-open" style={{ marginRight: '8px', color: '#2e496b' }} />
            Przewodnik gracza
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px' }}>
            {savedAt && (
              <span style={{ color: '#94a3b8' }}>Zapisano: {formatDate(savedAt)}</span>
            )}
            {isDirty && !loading && (
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>● Niezapisane zmiany</span>
            )}
            {!loading && (
              <span style={{ color: '#94a3b8' }}>{wordCount} słów</span>
            )}
          </div>
        </div>

        {/* Right: controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

          {/* View mode toggle */}
          <div style={{
            display: 'flex',
            background: '#f1f5f9',
            borderRadius: '6px',
            padding: '2px',
            gap: '1px',
          }}>
            {[
              { key: VIEWS.EDIT, icon: 'fa-pen', label: 'Edytor' },
              { key: VIEWS.SPLIT, icon: 'fa-columns', label: 'Podgląd na żywo' },
              { key: VIEWS.PREVIEW, icon: 'fa-eye', label: 'Podgląd' },
            ].map(({ key, icon, label }) => (
              <button
                key={key}
                type="button"
                title={label}
                onClick={() => setView(key)}
                style={{
                  padding: '5px 11px',
                  background: view === key ? '#ffffff' : 'transparent',
                  color: view === key ? '#2e496b' : '#64748b',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: view === key ? 700 : 400,
                  boxShadow: view === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                <i className={`fas ${icon}`} style={{ marginRight: '5px' }} />
                {label}
              </button>
            ))}
          </div>

          {/* Open player page */}
          <a
            href={routes.playerInfo.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '6px 12px',
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              whiteSpace: 'nowrap',
            }}
          >
            <i className="fas fa-external-link-alt" />
            Strona gracza
          </a>

          {/* Visibility toggle */}
          <label
            htmlFor="player-guide-visibility"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#ffffff',
              fontSize: '12px',
              color: '#334155',
              cursor: loading ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              id="player-guide-visibility"
              type="checkbox"
              checked={isVisibleToPlayers}
              disabled={loading}
              onChange={e => setIsVisibleToPlayers(e.target.checked)}
            />
            Widoczne dla sportowców
          </label>

          {/* Save button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            title="Ctrl+S / Cmd+S"
            style={{
              padding: '6px 18px',
              background: isDirty ? '#2e496b' : '#94a3b8',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: saving || loading ? 'not-allowed' : 'pointer',
              opacity: saving || loading ? 0.8 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'background 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            {saving
              ? <><i className="fas fa-spinner fa-spin" />Zapisuję...</>
              : <><i className="fas fa-save" />Zapisz</>
            }
          </button>
        </div>
      </div>

      {/* ── Error bar ── */}
      {error && (
        <div style={{
          color: '#ef4444',
          fontSize: '13px',
          padding: '8px 24px',
          background: '#fef2f2',
          borderBottom: '1px solid #fecaca',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexShrink: 0,
        }}>
          <i className="fas fa-exclamation-circle" />
          {error}
        </div>
      )}

      {/* ── Main area ── */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '28px', marginBottom: '12px', display: 'block' }} />
            Ładowanie treści...
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', background: '#f8fafc', overflow: 'hidden' }}>

          {/* Editor pane — always rendered to preserve Quill state */}
          <div style={{
            flex: 1,
            display: showEditor ? 'flex' : 'none',
            flexDirection: 'column',
            borderRight: showPreview ? '1px solid #e2e8f0' : 'none',
            background: '#ffffff',
          }}>
            <div style={{
              padding: '5px 16px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              background: '#f8fafc',
              borderBottom: '1px solid #f1f5f9',
              flexShrink: 0,
            }}>
              Edytor
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <div className="admin-page-editor">
              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={setContent}
                modules={quillModules}
                formats={quillFormats}
                theme="snow"
                style={{ minHeight: 'calc(100vh - 160px)' }}
              />
              </div>
            </div>
          </div>

          {/* Preview pane */}
          <div style={{
            flex: 1,
            display: showPreview ? 'flex' : 'none',
            flexDirection: 'column',
            background: '#ffffff',
          }}>
            <div style={{
              padding: '5px 16px',
              fontSize: '10px',
              fontWeight: 700,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              background: '#f8fafc',
              borderBottom: '1px solid #f1f5f9',
              flexShrink: 0,
            }}>
              Podgląd na żywo
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
              {content ? (
                // eslint-disable-next-line react/no-danger
                <div className="ql-editor" dangerouslySetInnerHTML={{ __html: content }} />
              ) : (
                <div style={{ color: '#94a3b8', textAlign: 'center', paddingTop: '60px' }}>
                  <i className="fas fa-file-alt" style={{ fontSize: '36px', display: 'block', marginBottom: '12px' }} />
                  Brak treści do wyświetlenia
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* ── Image resize / align overlay ── */}
      {selectedImg && showEditor && (
        <ImageResizer
          imgEl={selectedImg}
          quillEditor={quillRef.current?.getEditor()}
          onClose={() => setSelectedImg(null)}
          onDelete={handleDeleteSelectedImage}
        />
      )}
    </div>
  );
};

export default AdminPageContent;
